import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Navbar from '../components/Navbar';
import { Card, Button, Input, Badge, Modal, Textarea } from '../components/ui';
import {
  Users, Crown, Star, Trash2, Plus, Edit, Search,
  Loader2, Shield, DollarSign, RefreshCw, Database,
  Instagram, MapPin, Globe, AlertTriangle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const Admin = () => {
  const [adminKey, setAdminKey] = useState('');
  const [authenticated, setAuthenticated] = useState(false);
  const [activeTab, setActiveTab] = useState('users');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Data
  const [users, setUsers] = useState([]);
  const [artists, setArtists] = useState([]);
  const [stats, setStats] = useState(null);
  const [pricing, setPricing] = useState({ monthly_usd: '4.99', monthly_eur: '5.99', yearly_usd: '39.99', yearly_eur: '44.99' });

  // Modals
  const [showArtistModal, setShowArtistModal] = useState(false);
  const [editingArtist, setEditingArtist] = useState(null);
  const [artistForm, setArtistForm] = useState({
    name: '', studio_name: '', location: '', instagram: '', website: '', styles: '', rating: 0, review_count: 0
  });

  const adminRequest = async (endpoint, method = 'POST', body = {}) => {
    const res = await fetch(`${API_URL}${endpoint}`, {
      method,
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ admin_key: adminKey, ...body }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Request failed');
    }
    return res.json();
  };

  const handleLogin = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await adminRequest('/api/admin/list-users');
      setUsers(data.users || []);
      setAuthenticated(true);
    } catch (err) {
      setError('Invalid admin key');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const data = await adminRequest('/api/admin/list-users');
      setUsers(data.users || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchArtists = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/artists`);
      const data = await res.json();
      setArtists(Array.isArray(data) ? data : []);
    } catch (err) {
      setError('Failed to fetch artists');
    } finally {
      setLoading(false);
    }
  };

  const setPremium = async (email) => {
    try {
      await adminRequest('/api/admin/set-premium', 'POST', { email, plan: 'yearly' });
      setSuccess(`${email} is now premium!`);
      fetchUsers();
    } catch (err) {
      setError(err.message);
    }
  };

  const seedArtists = async () => {
    setLoading(true);
    try {
      const data = await adminRequest('/api/seed');
      setSuccess(`Seeded: ${data.artists_count} artists, ${data.reviews_count} reviews`);
      fetchArtists();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const saveArtist = async () => {
    setLoading(true);
    try {
      const artistData = {
        ...artistForm,
        styles: artistForm.styles.split(',').map(s => s.trim()).filter(Boolean),
        rating: parseFloat(artistForm.rating) || 0,
        review_count: parseInt(artistForm.review_count) || 0,
      };

      const res = await fetch(`${API_URL}/api/artists`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(artistData),
      });

      if (res.ok) {
        setSuccess('Artist saved!');
        setShowArtistModal(false);
        resetArtistForm();
        fetchArtists();
      } else {
        throw new Error('Failed to save artist');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const resetArtistForm = () => {
    setArtistForm({ name: '', studio_name: '', location: '', instagram: '', website: '', styles: '', rating: 0, review_count: 0 });
    setEditingArtist(null);
  };

  const openEditArtist = (artist) => {
    setArtistForm({
      name: artist.name || '',
      studio_name: artist.studio_name || '',
      location: artist.location || '',
      instagram: artist.instagram || '',
      website: artist.website || '',
      styles: (artist.styles || []).join(', '),
      rating: artist.rating || 0,
      review_count: artist.review_count || 0,
    });
    setEditingArtist(artist);
    setShowArtistModal(true);
  };

  const fetchStats = async () => {
    try {
      const data = await adminRequest('/api/admin/stats');
      setStats(data);
    } catch (err) {}
  };

  const deleteUser = async (email) => {
    if (!window.confirm(`Delete user ${email}? This cannot be undone.`)) return;
    try {
      await adminRequest('/api/admin/delete-user', 'POST', { email });
      setSuccess(`User ${email} deleted`);
      fetchUsers();
    } catch (err) { setError(err.message); }
  };

  const deleteArtist = async (artistId, name) => {
    if (!window.confirm(`Delete artist ${name}?`)) return;
    try {
      await adminRequest('/api/admin/delete-artist', 'POST', { artist_id: artistId });
      setSuccess(`Artist ${name} deleted`);
      fetchArtists();
    } catch (err) { setError(err.message); }
  };

  const updatePricing = async () => {
    try {
      const data = await adminRequest('/api/admin/update-packages', 'POST', pricing);
      setSuccess('Pricing updated!');
    } catch (err) { setError(err.message); }
  };

  useEffect(() => {
    if (authenticated && activeTab === 'dashboard') fetchStats();
    if (authenticated && activeTab === 'users') fetchUsers();
    if (authenticated && activeTab === 'artists') fetchArtists();
  }, [authenticated, activeTab]);

  // Auto-clear messages
  useEffect(() => {
    if (success) { const t = setTimeout(() => setSuccess(''), 3000); return () => clearTimeout(t); }
  }, [success]);
  useEffect(() => {
    if (error) { const t = setTimeout(() => setError(''), 5000); return () => clearTimeout(t); }
  }, [error]);

  // Login Screen
  if (!authenticated) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
          <Card className="p-8">
            <div className="text-center mb-8">
              <Shield className="w-12 h-12 text-primary mx-auto mb-4" />
              <h1 className="font-heading text-2xl">ADMIN PANEL</h1>
              <p className="text-muted-foreground text-sm mt-2">InkTrack Management Dashboard</p>
            </div>
            {error && <div className="p-3 bg-destructive/10 border border-destructive/30 rounded-sm mb-4 text-sm text-destructive">{error}</div>}
            <div className="space-y-4">
              <Input
                type="password"
                placeholder="Admin Key"
                value={adminKey}
                onChange={(e) => setAdminKey(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
              />
              <Button className="w-full" onClick={handleLogin} disabled={loading || !adminKey}>
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Login'}
              </Button>
            </div>
          </Card>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-3xl"><Shield className="w-6 h-6 inline mr-2 text-primary" />ADMIN PANEL</h1>
            <p className="text-muted-foreground text-sm">Manage users, artists, and settings</p>
          </div>
          <Button variant="outline" onClick={() => { setAuthenticated(false); setAdminKey(''); }}>
            Logout
          </Button>
        </div>

        {/* Messages */}
        {success && <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-sm mb-4 text-sm text-green-500">{success}</div>}
        {error && <div className="p-3 bg-destructive/10 border border-destructive/30 rounded-sm mb-4 text-sm text-destructive">{error}</div>}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-border">
          {[
            { id: 'dashboard', label: 'Dashboard', icon: Database },
            { id: 'users', label: 'Users', icon: Users },
            { id: 'artists', label: 'Artists', icon: Star },
            { id: 'pricing', label: 'Pricing', icon: DollarSign },
            { id: 'tools', label: 'Tools', icon: Shield },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-heading tracking-wider border-b-2 transition-colors ${
                activeTab === tab.id ? 'text-primary border-primary' : 'text-muted-foreground border-transparent'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-xl">Platform Statistics</h2>
              <Button variant="outline" size="sm" onClick={fetchStats}>
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />Refresh
              </Button>
            </div>
            {stats ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { label: 'Total Users', value: stats.total_users, icon: Users, color: 'text-blue-400' },
                  { label: 'Premium Users', value: stats.premium_users, icon: Crown, color: 'text-yellow-400' },
                  { label: 'Total Artists', value: stats.total_artists, icon: Star, color: 'text-green-400' },
                  { label: 'Total Tattoos', value: stats.total_tattoos, icon: Database, color: 'text-purple-400' },
                  { label: 'Total Reviews', value: stats.total_reviews, icon: Star, color: 'text-pink-400' },
                  { label: 'Reminders', value: stats.total_reminders, icon: AlertTriangle, color: 'text-orange-400' },
                  { label: 'Body Plans', value: stats.total_plans, icon: MapPin, color: 'text-cyan-400' },
                  { label: 'Transactions', value: stats.total_transactions, icon: DollarSign, color: 'text-emerald-400' },
                ].map((stat, i) => (
                  <Card key={i} className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <stat.icon className={`w-5 h-5 ${stat.color}`} />
                      <span className="font-heading text-2xl text-primary">{stat.value}</span>
                    </div>
                    <p className="text-xs text-muted-foreground">{stat.label}</p>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="text-center py-10"><Loader2 className="w-8 h-8 animate-spin text-primary mx-auto" /></div>
            )}
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-xl">{users.length} Users</h2>
              <Button variant="outline" size="sm" onClick={fetchUsers}>
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
            <div className="space-y-3">
              {users.map((user) => (
                <Card key={user.user_id} className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {user.picture ? (
                        <img src={user.picture} alt="" className="w-10 h-10 rounded-full" />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                          <span className="text-primary font-bold">{user.name?.charAt(0)}</span>
                        </div>
                      )}
                      <div>
                        <p className="font-medium">{user.name}</p>
                        <p className="text-sm text-muted-foreground">{user.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge variant={user.subscription_plan !== 'free' ? 'default' : 'secondary'}>
                        {user.subscription_plan || 'free'}
                      </Badge>
                      {user.is_admin && <Badge variant="warning">ADMIN</Badge>}
                      {user.subscription_plan === 'free' && (
                        <Button size="sm" variant="outline" onClick={() => setPremium(user.email)}>
                          <Crown className="w-3 h-3 mr-1" />
                          Set Premium
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => deleteUser(user.email)}>
                        <Trash2 className="w-3 h-3 text-destructive" />
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Artists Tab */}
        {activeTab === 'artists' && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-xl">{artists.length} Artists</h2>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={fetchArtists}>
                  <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
                <Button size="sm" onClick={() => { resetArtistForm(); setShowArtistModal(true); }}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Artist
                </Button>
              </div>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {artists.map((artist) => (
                <Card key={artist.artist_id} className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className="font-heading text-lg">{artist.name}</h3>
                      {artist.studio_name && <p className="text-sm text-muted-foreground">{artist.studio_name}</p>}
                    </div>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => openEditArtist(artist)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => deleteArtist(artist.artist_id, artist.name)}>
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-1 text-sm">
                    {artist.location && <div className="flex items-center gap-1 text-muted-foreground"><MapPin className="w-3 h-3" />{artist.location}</div>}
                    {artist.instagram && <div className="flex items-center gap-1 text-muted-foreground"><Instagram className="w-3 h-3" />{artist.instagram}</div>}
                    <div className="flex items-center gap-1 text-primary"><Star className="w-3 h-3 fill-current" />{(artist.rating ?? 0).toFixed(1)} ({artist.review_count || 0})</div>
                  </div>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {artist.styles?.slice(0, 3).map((s, i) => <Badge key={i} className="text-xs">{s}</Badge>)}
                  </div>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Pricing Tab */}
        {activeTab === 'pricing' && (
          <div>
            <h2 className="font-heading text-xl mb-6">Subscription Pricing</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="p-6">
                <h3 className="font-heading text-lg mb-4">Monthly Plan</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">USD Price</label>
                    <Input type="number" step="0.01" value={pricing.monthly_usd} onChange={(e) => setPricing({...pricing, monthly_usd: e.target.value})} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">EUR Price</label>
                    <Input type="number" step="0.01" value={pricing.monthly_eur} onChange={(e) => setPricing({...pricing, monthly_eur: e.target.value})} />
                  </div>
                </div>
              </Card>
              <Card className="p-6">
                <h3 className="font-heading text-lg mb-4">Yearly Plan</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">USD Price</label>
                    <Input type="number" step="0.01" value={pricing.yearly_usd} onChange={(e) => setPricing({...pricing, yearly_usd: e.target.value})} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">EUR Price</label>
                    <Input type="number" step="0.01" value={pricing.yearly_eur} onChange={(e) => setPricing({...pricing, yearly_eur: e.target.value})} />
                  </div>
                </div>
              </Card>
            </div>
            <Button className="mt-6" onClick={updatePricing} disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <DollarSign className="w-4 h-4 mr-2" />}
              Save Prices
            </Button>
          </div>
        )}

        {/* Tools Tab */}
        {activeTab === 'tools' && (
          <div className="space-y-6">
            <Card className="p-6">
              <h3 className="font-heading text-lg mb-4">Database Tools</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-muted rounded-sm">
                  <div>
                    <p className="font-medium">Re-seed Artists & Reviews</p>
                    <p className="text-sm text-muted-foreground">Replaces all artists with verified data (29 artists, 80+ reviews)</p>
                  </div>
                  <Button variant="outline" onClick={seedArtists} disabled={loading}>
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Database className="w-4 h-4 mr-2" />Seed</>}
                  </Button>
                </div>
                <div className="flex items-center justify-between p-4 bg-muted rounded-sm">
                  <div>
                    <p className="font-medium">Admin API Endpoint</p>
                    <p className="text-sm text-muted-foreground font-mono">{API_URL}/api/admin/</p>
                  </div>
                  <Badge>Active</Badge>
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <h3 className="font-heading text-lg mb-4">Quick Commands (cURL)</h3>
              <div className="space-y-3 text-sm font-mono bg-muted p-4 rounded-sm overflow-x-auto">
                <p className="text-muted-foreground"># Set user premium</p>
                <p className="text-green-400">curl -X POST {API_URL}/api/admin/set-premium -H "Content-Type: application/json" -d '{`{"admin_key":"YOUR_KEY","email":"user@email.com","plan":"yearly"}`}'</p>
                <p className="text-muted-foreground mt-3"># List users</p>
                <p className="text-green-400">curl -X POST {API_URL}/api/admin/list-users -H "Content-Type: application/json" -d '{`{"admin_key":"YOUR_KEY"}`}'</p>
                <p className="text-muted-foreground mt-3"># Seed artists</p>
                <p className="text-green-400">curl -X POST {API_URL}/api/seed -H "Content-Type: application/json" -d '{`{"admin_key":"YOUR_KEY"}`}'</p>
              </div>
            </Card>
          </div>
        )}

        {/* Artist Modal */}
        <Modal
          isOpen={showArtistModal}
          onClose={() => { setShowArtistModal(false); resetArtistForm(); }}
          title={editingArtist ? 'Edit Artist' : 'Add Artist'}
        >
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Name *</label>
              <Input value={artistForm.name} onChange={(e) => setArtistForm({...artistForm, name: e.target.value})} placeholder="Artist name" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Studio</label>
              <Input value={artistForm.studio_name} onChange={(e) => setArtistForm({...artistForm, studio_name: e.target.value})} placeholder="Studio name" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Location</label>
              <Input value={artistForm.location} onChange={(e) => setArtistForm({...artistForm, location: e.target.value})} placeholder="City, Country" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Instagram</label>
              <Input value={artistForm.instagram} onChange={(e) => setArtistForm({...artistForm, instagram: e.target.value})} placeholder="@handle" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Website</label>
              <Input value={artistForm.website} onChange={(e) => setArtistForm({...artistForm, website: e.target.value})} placeholder="https://..." />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Styles (comma separated)</label>
              <Input value={artistForm.styles} onChange={(e) => setArtistForm({...artistForm, styles: e.target.value})} placeholder="Blackwork, Geometric, Fine Line" />
            </div>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => { setShowArtistModal(false); resetArtistForm(); }}>Cancel</Button>
              <Button className="flex-1" onClick={saveArtist} disabled={loading || !artistForm.name}>
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
              </Button>
            </div>
          </div>
        </Modal>
      </main>
    </div>
  );
};

export default Admin;
